import { useState, useRef, useEffect } from 'react';
import { Pencil } from 'lucide-react';

interface InlineEditProps {
  value: string;
  onSave: (value: string) => void;
  className?: string;
  inputClassName?: string;
  type?: 'text' | 'currency' | 'email' | 'url' | 'tel';
  multiline?: boolean;
  placeholder?: string;
}

function validate(value: string, type: InlineEditProps['type']): boolean {
  if (!value.trim()) return true; // empty allowed (handled by save())
  switch (type) {
    case 'email':
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    case 'url':
      return /^https?:\/\/.+/.test(value);
    case 'tel':
      return /^[+\d\s().-]{4,}$/.test(value);
    default:
      return true;
  }
}

/**
 * Click-to-edit inline field. Displays text normally; on click, swaps to an input.
 * Saves on Enter/blur, cancels on Escape.
 *
 * For type="currency", value/onSave work in cents; display is in dollars.
 * For type="email"|"url"|"tel", validation runs on save; invalid input shows red border.
 * For multiline=true, renders a <textarea>; Enter inserts newline (not save).
 */
export default function InlineEdit({
  value,
  onSave,
  className = '',
  inputClassName = '',
  type = 'text',
  multiline = false,
  placeholder = '—',
}: InlineEditProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [invalid, setInvalid] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editing) {
      if (multiline) {
        textareaRef.current?.focus();
        textareaRef.current?.select();
      } else {
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    }
  }, [editing, multiline]);

  const startEditing = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (type === 'currency') {
      const cents = parseInt(value, 10);
      setDraft(isNaN(cents) || cents === 0 ? '' : (cents / 100).toFixed(2));
    } else {
      setDraft(value);
    }
    setInvalid(false);
    setEditing(true);
  };

  const save = () => {
    if (type === 'currency') {
      const dollars = parseFloat(draft);
      const cents = isNaN(dollars) ? '' : String(Math.round(dollars * 100));
      if (cents !== value) onSave(cents);
      setEditing(false);
      return;
    }
    const trimmed = draft.trim();
    if (trimmed && !validate(trimmed, type)) {
      setInvalid(true);
      return; // stay editing
    }
    setInvalid(false);
    if (trimmed && trimmed !== value) onSave(trimmed);
    setEditing(false);
  };

  const cancel = () => {
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      save();
    } else if (e.key === 'Escape') {
      cancel();
    }
  };

  if (editing) {
    const baseClass = `bg-transparent border rounded px-1.5 py-0.5 text-sm outline-none focus:ring-1 focus:ring-ring w-full ${
      invalid ? 'border-destructive focus:ring-destructive' : 'border-border'
    } ${inputClassName}`;

    if (multiline) {
      return (
        <textarea
          ref={textareaRef}
          rows={3}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (invalid) setInvalid(false);
          }}
          onBlur={save}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Escape') cancel();
            // Enter inserts newline in multiline mode (do not save)
          }}
          onClick={(e) => e.stopPropagation()}
          className={`${baseClass} resize-y min-h-[4rem]`}
          placeholder={placeholder}
        />
      );
    }

    const inputType = type === 'currency' ? 'number' : (type ?? 'text');

    return (
      <input
        ref={inputRef}
        type={inputType}
        step={type === 'currency' ? '0.01' : undefined}
        min={type === 'currency' ? '0' : undefined}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          if (invalid) setInvalid(false);
        }}
        onBlur={save}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
        className={baseClass}
        placeholder={placeholder}
      />
    );
  }

  const displayValue = (() => {
    if (type === 'currency') {
      const cents = parseInt(value, 10);
      if (isNaN(cents) || cents === 0) return placeholder;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(cents / 100);
    }
    return value || placeholder;
  })();

  if (multiline) {
    return (
      <div
        onClick={startEditing}
        className={`group/edit block whitespace-pre-wrap gap-1 cursor-text rounded px-1.5 py-0.5 -mx-1.5 -my-0.5 decoration-muted-foreground/30 underline decoration-dashed underline-offset-4 hover:bg-accent hover:decoration-muted-foreground/50 transition-colors ${className}`}
        title="Click to edit"
      >
        {displayValue}
      </div>
    );
  }

  return (
    <span
      onClick={startEditing}
      className={`group/edit inline-flex items-center gap-1 cursor-text rounded px-1.5 py-0.5 -mx-1.5 -my-0.5 decoration-muted-foreground/30 underline decoration-dashed underline-offset-4 hover:bg-accent hover:decoration-muted-foreground/50 transition-colors ${className}`}
      title="Click to edit"
    >
      {displayValue}
      <Pencil className="size-3 shrink-0 text-muted-foreground/0 group-hover/edit:text-muted-foreground/50 transition-colors" />
    </span>
  );
}
